import { Target, Eye, Heart, Users, Award, Lightbulb, Shield, Handshake } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/de753d50-e73a-4c45-bc5e-daf327ccb96f/images/e7b8d222ae05c521d43cde7a5be8d7c696bd7578c950beafd87c424fbd163a0d.png";

const teamMembers = [
  { name: "Rajesh Kumar", role: "Founder & CEO", initials: "RK" },
  { name: "Priya Sharma", role: "Head of Operations", initials: "PS" },
  { name: "Anil Verma", role: "Director - Client Relations", initials: "AV" },
  { name: "Sneha Patel", role: "Lead - Talent Acquisition", initials: "SP" },
];

const values = [
  { icon: Heart, title: "Integrity", desc: "Transparent and ethical practices in every engagement." },
  { icon: Award, title: "Excellence", desc: "Delivering quality outcomes that exceed expectations." },
  { icon: Lightbulb, title: "Innovation", desc: "Leveraging technology to enhance recruitment." },
  { icon: Shield, title: "Compliance", desc: "Strict adherence to labor laws and statutory requirements." },
  { icon: Users, title: "People First", desc: "Putting candidates and clients at the heart of everything." },
  { icon: Handshake, title: "Partnership", desc: "Building long-term relationships, not transactions." },
];

export default function AboutPage() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-[var(--sb-blue)] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal>
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-orange)] mb-4 block">Who We Are</span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase mb-6">About Skill Bridge</h1>
            <p className="text-blue-200 text-base md:text-lg max-w-2xl leading-relaxed">
              A trusted human resources partner dedicated to bridging the gap between exceptional talent and outstanding organizations across India.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Story */}
      <section data-testid="about-story" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-4 block">Our Story</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase mb-6">Built on a Simple Belief</h2>
              <p className="text-slate-600 text-base leading-relaxed mb-4">
                Skill Bridge Human Resources Private Limited was founded with a clear mission: to simplify recruitment and create meaningful connections between businesses and professionals.
              </p>
              <p className="text-slate-600 text-base leading-relaxed mb-4">
                Starting from a small office, we've grown into a full-service HR solutions provider operating across 15+ industries and multiple states in India. Our journey has been fueled by the trust of over 120 client companies and the careers of 500+ professionals we've helped place.
              </p>
              <p className="text-slate-600 text-base leading-relaxed">
                Today, we combine deep industry expertise with modern recruitment technology to deliver staffing solutions that are precise, fast, and fully compliant.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={2}>
              <img src={ABOUT_IMG} alt="About Skill Bridge" className="w-full max-w-md mx-auto" />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal>
              <div className="bg-white border border-slate-200 p-8 md:p-10 h-full">
                <div className="w-14 h-14 bg-[var(--sb-blue)] flex items-center justify-center mb-6 text-white">
                  <Target className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 uppercase mb-4">Our Mission</h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  To be India's most reliable HR partner by delivering customized, compliant, and efficient manpower solutions that empower businesses to grow and professionals to thrive.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={1}>
              <div className="bg-white border border-slate-200 p-8 md:p-10 h-full">
                <div className="w-14 h-14 bg-[var(--sb-green)] flex items-center justify-center mb-6 text-white">
                  <Eye className="w-7 h-7" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 uppercase mb-4">Our Vision</h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  To become a nationally recognized leader in HR solutions, setting the benchmark for quality, innovation, and ethical recruitment practices in the Indian staffing industry.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Team */}
      <section data-testid="about-team" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">Leadership</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Our Team</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, i) => (
              <ScrollReveal key={member.name} delay={i}>
                <div className="bg-white border border-slate-200 p-8 text-center transition-all duration-300 hover:shadow-[0_10px_40px_-15px_rgba(26,95,168,0.15)] hover:-translate-y-1">
                  <div className="w-20 h-20 bg-[var(--sb-blue)] text-white flex items-center justify-center mx-auto mb-5 text-2xl font-heading font-bold">
                    {member.initials}
                  </div>
                  <h4 className="font-heading text-lg font-bold text-slate-900 uppercase">{member.name}</h4>
                  <p className="text-slate-500 text-sm mt-1">{member.role}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="py-20 md:py-32 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">What Drives Us</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Our Values</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((val, i) => (
              <ScrollReveal key={val.title} delay={i}>
                <div className="bg-white border border-slate-200 p-8 transition-all duration-300 hover:shadow-[0_10px_40px_-15px_rgba(26,95,168,0.15)] hover:-translate-y-1 hover:border-[var(--sb-blue)]/30 h-full">
                  <div className="w-12 h-12 bg-slate-50 flex items-center justify-center mb-5 text-[var(--sb-blue)]">
                    <val.icon className="w-6 h-6" />
                  </div>
                  <h4 className="font-heading text-lg font-bold text-slate-900 uppercase mb-2">{val.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{val.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
